import { useState, type ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import Fab from '@mui/material/Fab'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import CircularProgress from '@mui/material/CircularProgress'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Slide from '@mui/material/Slide'
import Divider from '@mui/material/Divider'
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded'
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded'
import { useAuth } from '../../shared/contexts/AuthContext'
import { useCondominium } from '../../shared/contexts/CondominioContext'
import { useNotification } from '../../shared/contexts/NotificationContext'
import { api } from '../../shared/services/API'
import {
  SUPPORT_ACCEPTED_FILES,
  SUPPORT_BASE,
  SUPPORT_DESCRICAO_MAX,
  SUPPORT_MAX_FILE_BYTES,
  SUPPORT_MAX_TOTAL_BYTES,
  SUPPORT_PAGE_PATH,
  SUPPORT_TITULO_MAX,
  supportApiError,
} from './support'

const panelZ = (theme: { zIndex: { drawer: number } }) => theme.zIndex.drawer + 2

/** O tema força padding 10px 20px / 0.9375rem em todos os Button — encolhe os do formulário. */
const compactButtonSx = {
  py: 0.5,
  px: 1.5,
  fontSize: '0.8125rem',
  minHeight: 0,
  '& .MuiButton-startIcon > *': { fontSize: 16 },
} as const

/**
 * Widget de help desk / suporte — botão flutuante no canto inferior direito que
 * abre um formulário para submeter um novo pedido. O acompanhamento dos pedidos
 * já submetidos é feito na página `/support` (botão "ver pedidos anteriores").
 *
 * Montado globalmente no `Wrapper`, mas só aparece quando há um condomínio
 * activo — os anexos passam pela camada de arquivos, que exige esse contexto.
 */
export default function SupportWidget() {
  const { t } = useTranslation(['support', 'common'])
  const { isAuthenticated } = useAuth()
  const { activeCondominium } = useCondominium()
  const { notify } = useNotification()
  const navigate = useNavigate()

  const [open, setOpen] = useState(false)
  const [titulo, setTitulo] = useState('')
  const [descricao, setDescricao] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [submitting, setSubmitting] = useState(false)

  if (!isAuthenticated || !activeCondominium) return null

  const resetForm = () => {
    setTitulo('')
    setDescricao('')
    setFiles([])
  }

  const onPickFiles = (event: ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(event.target.files ?? [])
    event.target.value = ''
    const ok: File[] = []
    for (const file of picked) {
      if (file.size > SUPPORT_MAX_FILE_BYTES) {
        notify(t('form.tooLarge', { name: file.name }), 'warning')
        continue
      }
      ok.push(file)
    }
    if (ok.length) setFiles((prev) => [...prev, ...ok])
  }

  const removeFile = (index: number) => setFiles((prev) => prev.filter((_, i) => i !== index))

  const handleSubmit = async () => {
    const subject = titulo.trim()
    const message = descricao.trim()
    if (!subject) {
      notify(t('form.subjectRequired'), 'warning')
      return
    }
    if (!message) {
      notify(t('form.messageRequired'), 'warning')
      return
    }
    if (files.reduce((sum, file) => sum + file.size, 0) > SUPPORT_MAX_TOTAL_BYTES) {
      notify(t('form.tooManyBytes'), 'warning')
      return
    }
    setSubmitting(true)
    try {
      const body = new FormData()
      body.append('titulo', subject)
      body.append('descricao', message)
      files.forEach((file) => body.append('anexos', file))
      await api.post(SUPPORT_BASE, body)
      notify(t('form.success'))
      resetForm()
      setOpen(false)
    } catch (error) {
      notify(supportApiError(error, t('form.error')), 'error')
    } finally {
      setSubmitting(false)
    }
  }

  const goToTickets = () => {
    setOpen(false)
    navigate(SUPPORT_PAGE_PATH)
  }

  return (
    <>
      <Fab
        color="primary"
        aria-label={t('fab')}
        onClick={() => setOpen((prev) => !prev)}
        sx={{
          position: 'fixed',
          bottom: { xs: 16, sm: 24 },
          right: { xs: 16, sm: 24 },
          zIndex: panelZ,
        }}
      >
        {open ? <CloseRoundedIcon /> : <SupportAgentRoundedIcon />}
      </Fab>

      <Slide direction="up" in={open} mountOnEnter unmountOnExit>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: { xs: 84, sm: 96 },
            right: { xs: 16, sm: 24 },
            left: { xs: 16, sm: 'auto' },
            width: { xs: 'auto', sm: 380 },
            maxHeight: 'calc(100vh - 140px)',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 1.5,
            overflow: 'hidden',
            zIndex: panelZ,
          }}
        >
          {/* Cabeçalho */}
          <Box
            sx={{
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              px: 2,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <SupportAgentRoundedIcon fontSize="small" />
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {t('form.title')}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                {t('subtitle')}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => setOpen(false)}
              sx={{ color: 'inherit' }}
              aria-label={t('common:actions.close', 'Fechar')}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Corpo — formulário de novo pedido */}
          <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
            <Stack spacing={1.5}>
              <TextField
                label={t('form.subject')}
                placeholder={t('form.subjectPlaceholder')}
                value={titulo}
                onChange={(event) => setTitulo(event.target.value)}
                size="small"
                fullWidth
                slotProps={{ htmlInput: { maxLength: SUPPORT_TITULO_MAX } }}
              />

              <TextField
                label={t('form.message')}
                placeholder={t('form.messagePlaceholder')}
                value={descricao}
                onChange={(event) => setDescricao(event.target.value)}
                size="small"
                fullWidth
                multiline
                minRows={4}
                slotProps={{ htmlInput: { maxLength: SUPPORT_DESCRICAO_MAX } }}
              />

              <Box>
                <Button
                  size="small"
                  variant="outlined"
                  component="label"
                  startIcon={<AttachFileRoundedIcon />}
                  sx={compactButtonSx}
                >
                  {t('form.addFiles')}
                  <input type="file" hidden multiple accept={SUPPORT_ACCEPTED_FILES} onChange={onPickFiles} />
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {t('form.accepted')}
                </Typography>
                {files.length > 0 && (
                  <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap', mt: 1 }}>
                    {files.map((file, index) => (
                      <Chip
                        key={`${file.name}-${index}`}
                        label={file.name}
                        size="small"
                        onDelete={() => removeFile(index)}
                        sx={{ maxWidth: 200 }}
                      />
                    ))}
                  </Stack>
                )}
              </Box>

              <Button
                size="small"
                variant="contained"
                onClick={handleSubmit}
                disabled={submitting}
                startIcon={
                  submitting ? <CircularProgress size={14} color="inherit" /> : <SendRoundedIcon />
                }
                sx={{ ...compactButtonSx, alignSelf: 'flex-start' }}
              >
                {submitting ? t('form.submitting') : t('form.submit')}
              </Button>
            </Stack>
          </Box>

          <Divider />
          <Button
            fullWidth
            startIcon={<HistoryRoundedIcon fontSize="small" />}
            onClick={goToTickets}
            sx={{ borderRadius: 0, py: 1.25, color: 'text.secondary', fontWeight: 500 }}
          >
            {t('viewAll')}
          </Button>
        </Paper>
      </Slide>
    </>
  )
}
